import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router'
import { useParams } from 'react-router-dom'

import { Link } from '@driftt/dds'

import { Metadata } from 'components/Metadata'
import { driftLoadingStateBuilder } from 'components/States'
import { useAccountDetails } from 'hooks/useAccountDetails'
import { segmentTrack, TrackType } from 'utils/tracking'
import { getURLSearchParam, getURLSearchParams } from 'utils/urlUtils'

import AccountDetailsHeader from './AccountDetailsHeader'
import AttributesTab from './AttributesTab'
import ContactsTab from './ContactsTab'
import FeedTab from './FeedTab'
import OpportunitiesTab from './OpportunitiesTab'

import styles from './styles.module.css'

export interface AccountDetailsPageParams {
  id: string
}

const TAB_SEARCH_PARAM_KEY = 'tab'
const DEFAULT_TAB = 'Activity'

const AccountDetailsPage = () => {
  const { id: accountId } = useParams<AccountDetailsPageParams>()

  const {
    hasSfdc,
    isLoading,
    accountData,
    companyData,
    salesforceData,
    explainData,
    opportunities,
    opportunitiesError,
    accountFitScore,
    accountScoreBreakdown
  } = useAccountDetails(accountId)
  const history = useHistory()
  const initialTab = getURLSearchParam(TAB_SEARCH_PARAM_KEY) || DEFAULT_TAB

  const [activeTab, setActiveTab] = useState<string>(initialTab)

  const tabs = [
    'Activity',
    'Attributes',
    'Contacts',
    hasSfdc && accountData && 'Opportunities'
  ].filter(Boolean) as string[]

  const LoadingComponent = driftLoadingStateBuilder('Loading account page...')

  useEffect(() => {
    const params = getURLSearchParams()
    params.set(TAB_SEARCH_PARAM_KEY, activeTab)
    history.replace({
      search: params.toString(),
      hash: history.location.hash
    })
  }, [history, activeTab])

  useEffect(() => {
    if (accountId) {
      segmentTrack(TrackType.ACCOUNT_DETAILS_PAGE_ACCESSED, {
        accountId,
        companyName: companyData?.name
      })
    }
  }, [accountId, companyData])

  if (isLoading || hasSfdc === undefined) {
    return (
      <div className={styles.emptyPage}>
        <div className={styles.loader}>
          <LoadingComponent />
        </div>
      </div>
    )
  }

  let tabContent = <FeedTab companyData={companyData} explainData={explainData} />
  switch (activeTab) {
    case 'Attributes':
      tabContent = (
        <AttributesTab
          account={accountData}
          company={companyData}
          sfdcAccounts={salesforceData?.accounts}
        />
      )
      break
    case 'Contacts':
      tabContent = <ContactsTab accountId={accountId} accountName={accountData?.name} />
      break
    case 'Opportunities':
      tabContent = (
        <OpportunitiesTab
          accountId={accountId}
          salesforceData={salesforceData}
          opportunities={opportunities}
          opportunitiesError={opportunitiesError}
        />
      )
      break
    default:
      break
  }

  const numOfOpportunities =
    hasSfdc && accountData && opportunities ? (
      <Link onClick={() => setActiveTab('Opportunities')}>{opportunities?.length}</Link>
    ) : (
      undefined
    )

  return (
    <div id="accountDetailsPage" className={styles.accountDetailsPage}>
      <Metadata title={`Drift | ${accountData?.name ?? 'Account details'}`} />
      <AccountDetailsHeader
        account={accountData}
        activeTab={activeTab}
        tabNames={tabs}
        onTabSelected={setActiveTab}
        explainData={explainData}
        company={companyData}
        salesforceData={salesforceData}
        accountFitScore={accountFitScore}
        numOfOpportunities={numOfOpportunities}
        scoreBreakdown={accountScoreBreakdown}
      />
      <div className={styles.body}>{tabContent}</div>
    </div>
  )
}

export default AccountDetailsPage
